/* =========================================================================
   The ichthys school — ambient background motion for The Chosen content.

   Beta feedback (Eric 2026-08-21): the play screen "feels a bit boring" —
   it is a chip, a meter and four buttons on a flat gradient, and nothing on
   it moves while the player reads. This drifts The Chosen's three-fish mark
   through the background of that screen, but ONLY while the content on it is
   Chosen content: any individual question whose category is "The Chosen" (so a
   Chosen question surfacing inside a Mixed round gets it too), and the round
   intro for a round that is Chosen content end to end — Round 3 for everyone,
   or any round where the player picked that category.

   Deliberately NOT on the round picker's tiles: the fish are atmosphere for
   the content, not decoration on a control.

   The layer is inert by construction — aria-hidden, pointer-events:none, no
   focusable nodes, painted below the screen's own children — and is never
   mounted at all under prefers-reduced-motion, so a player who asks for less
   motion gets today's screens byte-for-byte.
   ========================================================================= */
import { syncAmbientLayer } from './ambientLayer';

/** Class on the mounted layer. Own prefix, deliberately not `rp-`/`rpk-`:
    this module mounts INTO other screens, so a name collision here would be
    the invisible-round-picker bug all over again. */
export const FISH_LAYER_CLASS = 'fsch';

/** One fish's fixed slot in the composition. */
export interface FishLane {
  /** Vertical position, % of the host's height. */
  top: number;
  /** Seconds for one full crossing — nearer (bigger) fish cross faster. */
  dur: number;
  /** Negative seconds: how far into its crossing the fish starts, so the
      school is already in the water when the screen appears. */
  delay: number;
  scale: number;
  opacity: number;
  /** true = swims right-to-left; the sprite mirrors to match. */
  rtl: boolean;
  /** true = the logo's charcoal fish (reads as further away). */
  dim: boolean;
}

/**
 * The composition. A fixed table rather than Math.random() so the layout is
 * art-directed and assertable in tests.
 *
 * Opacities are tuned for the PLAY screen's ground (the app's plum gradient),
 * not for an overlay scrim — the first pass was set for a 74%-black scrim and
 * was effectively invisible on the real screen (Eric 2026-08-21: "I am not
 * able to see the fish"). Lanes avoid 36–62%, where the question body sits.
 */
export const FISH_LANES: readonly FishLane[] = [
  { top:  9, dur: 30, delay:  -4, scale: 1.00, opacity: 0.30, rtl: false, dim: true  },
  { top: 19, dur: 22, delay: -15, scale: 1.55, opacity: 0.46, rtl: false, dim: false },
  { top: 29, dur: 38, delay:  -9, scale: 0.80, opacity: 0.24, rtl: true,  dim: true  },
  { top: 68, dur: 26, delay: -19, scale: 1.30, opacity: 0.40, rtl: false, dim: false },
  { top: 78, dur: 34, delay:  -2, scale: 0.90, opacity: 0.26, rtl: true,  dim: true  },
  { top: 87, dur: 20, delay: -11, scale: 1.75, opacity: 0.44, rtl: false, dim: false },
  { top: 95, dur: 36, delay: -26, scale: 1.10, opacity: 0.28, rtl: true,  dim: true  },
];

/** The Chosen's mark traced once as a <symbol>: lens body + notched tail.
    Two gradients stand in for the logo's teal and charcoal fish, both lifted
    to read against the plum ground — the logo's true charcoal disappears on
    it entirely. */
const FISH_DEFS =
  `<svg class="fsch-defs" aria-hidden="true" focusable="false">` +
  `<defs>` +
  `<linearGradient id="fschTeal" x1="0" y1="1" x2="1" y2="0">` +
  `<stop offset="0" stop-color="#2FA79A"/><stop offset="1" stop-color="#7FF2DA"/>` +
  `</linearGradient>` +
  `<linearGradient id="fschDim" x1="0" y1="1" x2="1" y2="0">` +
  `<stop offset="0" stop-color="#6E6688"/><stop offset="1" stop-color="#B6ADD4"/>` +
  `</linearGradient>` +
  `<symbol id="fschSym" viewBox="0 0 62 24">` +
  `<path d="M17 12C26 2.6 44 0.6 60 12 44 23.4 26 21.4 17 12Z"/>` +
  `<path d="M17.4 12 3 4.2 7.4 12 3 19.8Z"/>` +
  `</symbol>` +
  `</defs></svg>`;

/**
 * True when a category label or picker groupKey names The Chosen. Tolerates
 * both spellings the app uses — the question data's display string
 * ("The Chosen") and the picker's slug ("the-chosen"/"chosen") — plus casing
 * and spacing drift from the server. PURE.
 */
export function isChosenCategory(value: string | null | undefined): boolean {
  if (typeof value !== 'string') return false;
  const v = value.trim().toLowerCase().replace(/[-_]+/g, ' ').replace(/\s+/g, ' ');
  return v === 'the chosen' || v === 'chosen';
}

/**
 * True when a whole ROUND is Chosen content, for the round intro — which runs
 * before any question is served and so has no `q.category` to gate on.
 *
 * Two ways a round qualifies. The picker's `groupKey` is the direct one, but
 * it is null for guests and for anyone below the picker's unlock, and Round 3
 * ("The Chosen — Season 1") is Chosen content for *everyone* — so the round's
 * own question categories are the fallback. A simple majority, not `.some()`:
 * one Chosen question drawn into a Mixed round should light up that question,
 * not advertise the whole round as Chosen. PURE.
 */
export function isChosenRound(
  groupKey: string | null | undefined,
  categories: readonly string[],
): boolean {
  if (isChosenCategory(groupKey)) return true;
  if (!categories.length) return false;
  const chosen = categories.filter(isChosenCategory).length;
  return chosen * 2 > categories.length;
}

/** Markup for the school. PURE — callers decide whether to show it. */
export function fishLayerHtml(): string {
  const fish = FISH_LANES.map(
    (l) =>
      `<div class="fsch-i${l.rtl ? ' rtl' : ''}" style="` +
      `top:${l.top}%;` +
      `animation-duration:${l.dur}s;` +
      `animation-delay:${l.delay}s;` +
      `opacity:${l.opacity};` +
      `--fsch-s:${l.scale}` +
      `">` +
      `<div class="fsch-b">` +
      `<svg class="fsch-g" viewBox="0 0 62 24" aria-hidden="true" focusable="false">` +
      `<use href="#fschSym" fill="url(#${l.dim ? 'fschDim' : 'fschTeal'})"/>` +
      `</svg>` +
      `</div></div>`,
  ).join('');
  return FISH_DEFS + fish;
}

/**
 * Put the school in `host` when `on`, take it out when not. Idempotent: an
 * already-correct host is left completely alone, so calling this on every
 * served question never restarts the animation mid-crossing (which would read
 * as the fish teleporting between questions).
 *
 * Returns true when the school is mounted afterwards — handy for tests and
 * for callers that want to know whether reduced motion suppressed it.
 */
export function syncFishSchool(host: HTMLElement | null, on: boolean): boolean {
  return syncAmbientLayer(host, FISH_LAYER_CLASS, fishLayerHtml, on);
}
